import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { LiveRates } from '@/lib/priceCalculator';
import { GemstoneType, GoldPurity } from '@/types/jewelry';

const STORAGE_KEY = 'aurum_rate_overrides_v1';

export const GOLD_PURITIES: GoldPurity[] = ['24K', '22K', '18K', '14K', '10K'];
export const GEM_TYPES: Exclude<GemstoneType, 'None'>[] = [
  'Diamond', 'Ruby', 'Emerald', 'Sapphire', 'Pearl', 'Amethyst', 'Topaz', 'Opal',
];

export interface RateOverrides {
  /** Per-gram USD overrides keyed by purity. Missing/null = use live rate. */
  goldPerGram: Partial<Record<GoldPurity, number>>;
  /** Per-gram USD override. null = use live rate. */
  silverPerGram: number | null;
  /** Per-carat USD overrides keyed by gem type. */
  gemPerCarat: Partial<Record<Exclude<GemstoneType, 'None'>, number>>;
  /** Master toggle: when false, overrides are ignored even if values are set. */
  enabled: boolean;
}

const EMPTY: RateOverrides = {
  goldPerGram: {},
  silverPerGram: null,
  gemPerCarat: {},
  enabled: false,
};

interface Ctx {
  overrides: RateOverrides;
  setEnabled: (v: boolean) => void;
  setGold: (purity: GoldPurity, value: number | null) => void;
  setSilver: (value: number | null) => void;
  setGem: (gem: Exclude<GemstoneType, 'None'>, value: number | null) => void;
  clearAll: () => void;
  /** True if at least one override value is set (regardless of enabled flag). */
  hasAny: boolean;
  /** True if overrides are enabled AND at least one is set. */
  isActive: boolean;
}

const RateOverridesContext = createContext<Ctx | null>(null);

function load(): RateOverrides {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return {
      goldPerGram: parsed.goldPerGram ?? {},
      silverPerGram: parsed.silverPerGram ?? null,
      gemPerCarat: parsed.gemPerCarat ?? {},
      enabled: !!parsed.enabled,
    };
  } catch { return EMPTY; }
}
function save(o: RateOverrides) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(o)); } catch { /* noop */ }
}

export function RateOverridesProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<RateOverrides>(EMPTY);

  useEffect(() => { setOverrides(load()); }, []);

  const update = useCallback((next: RateOverrides) => {
    setOverrides(next);
    save(next);
  }, []);

  const setEnabled = useCallback((v: boolean) => update({ ...load(), enabled: v }), [update]);
  const setGold = useCallback((purity: GoldPurity, value: number | null) => {
    const cur = load();
    const goldPerGram = { ...cur.goldPerGram };
    if (value == null || !Number.isFinite(value) || value <= 0) delete goldPerGram[purity];
    else goldPerGram[purity] = value;
    update({ ...cur, goldPerGram });
  }, [update]);
  const setSilver = useCallback((value: number | null) => {
    const cur = load();
    update({ ...cur, silverPerGram: value != null && Number.isFinite(value) && value > 0 ? value : null });
  }, [update]);
  const setGem = useCallback((gem: Exclude<GemstoneType, 'None'>, value: number | null) => {
    const cur = load();
    const gemPerCarat = { ...cur.gemPerCarat };
    if (value == null || !Number.isFinite(value) || value <= 0) delete gemPerCarat[gem];
    else gemPerCarat[gem] = value;
    update({ ...cur, gemPerCarat });
  }, [update]);
  const clearAll = useCallback(() => update(EMPTY), [update]);

  const hasAny = useMemo(() => (
    Object.keys(overrides.goldPerGram).length > 0 ||
    overrides.silverPerGram != null ||
    Object.keys(overrides.gemPerCarat).length > 0
  ), [overrides]);

  const isActive = overrides.enabled && hasAny;

  const value = useMemo<Ctx>(() => ({
    overrides, setEnabled, setGold, setSilver, setGem, clearAll, hasAny, isActive,
  }), [overrides, setEnabled, setGold, setSilver, setGem, clearAll, hasAny, isActive]);

  return <RateOverridesContext.Provider value={value}>{children}</RateOverridesContext.Provider>;
}

export function useRateOverrides() {
  const ctx = useContext(RateOverridesContext);
  if (!ctx) throw new Error('useRateOverrides must be used inside RateOverridesProvider');
  return ctx;
}

/**
 * Merge manual overrides into a base LiveRates object. Overrides win field-by-field.
 * Returns a new object; caller must invoke this BEFORE FX conversion so overrides
 * (always entered in USD) get converted to the user's selected display currency.
 */
export function applyOverrides(base: LiveRates | null, ov: RateOverrides): LiveRates | null {
  if (!base) return base;
  if (!ov.enabled) return base;
  const goldPerGram = { ...base.goldPerGram };
  for (const [k, v] of Object.entries(ov.goldPerGram)) {
    if (v != null) goldPerGram[k as GoldPurity] = v;
  }
  const gemPerCarat = { ...base.gemPerCarat };
  for (const [k, v] of Object.entries(ov.gemPerCarat)) {
    if (v != null) gemPerCarat[k as GemstoneType] = v;
  }
  return {
    ...base,
    goldPerGram,
    silverPerGram: ov.silverPerGram ?? base.silverPerGram,
    gemPerCarat,
  };
}
